import React, { useMemo } from "react";
import { debounce } from "lodash";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { Grid, TextField } from "@material-ui/core";
import { useTypedDispatch } from "../../../app/state";
import HorizontalPanel from "../../../components/HorizontalPanel";
import Multiselect from "../../../components/Multiselect";
import { selectCurrentConfiguration } from "../../../store/deviceConfigurationStore/deviceConfigurationSelectors";
import { selectCurrentDevice } from "../../../store/deviceStore/deviceSelectors";
import ShortUserModel from "../../../models/ShortUserModel";
import { getWorkflowUserList } from "../../../store/userListStore/userListSelectors";
import UserModel from "../../../models/UserModel";
import { assignDevice, updateDeviceOperators, updateDeviceOwners } from "../../../store/deviceStore/deviceRequests";
import UserSelectionInput from "../../../components/UserSelectionInput";

import classes from './index.module.scss';

interface DeviceOwnershipProps {
  formDisabled?: boolean;
  onUpdate: () => any;
}

function getUserList(userList: UserModel[] | ShortUserModel[] | undefined) {
  return (userList || []).map((user) => {
    return {
      id: user.id,
      name: user.profile.fullName,
    }
  });
}

const DeviceOwnership = (props: DeviceOwnershipProps) => {
  const dispatch = useTypedDispatch();
  const selectedDevice = useSelector(selectCurrentDevice);
  const currentConfiguration = useSelector(selectCurrentConfiguration) || selectedDevice?.configuration;
  const isHistorySelected = Boolean(currentConfiguration?.parentDeviceConfiguration);
  const managerList = useSelector(getWorkflowUserList) || [];
  const currentOwnership = currentConfiguration?.deviceOwnership;

  const initialValues = {
    requester: currentOwnership?.requester?.profile?.fullName || '',
    lastUpdatedBy: currentOwnership?.lastUpdatedBy?.profile?.fullName || '',
    deviceOwnerList: (currentOwnership?.deviceOwnerList || []).map((user) => user.id),
    deviceOperatorList: (currentOwnership?.deviceOperatorList || []).map((user) => user.id),
    deviceFrequencyManagerList: (currentOwnership?.deviceFrequencyManagerList || []).map((user) => user.id),
  }

  const deviceOwnerList = getUserList(currentOwnership?.deviceOwnerList);
  const deviceOperatorList = getUserList(currentOwnership?.deviceOperatorList);
  const deviceFrequencyManagerList = getUserList(managerList);

  const ownershipFormik = useFormik({
    initialValues,
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const onUpdateDeviceOwners = async (userIdList: number[]) => {
    if (!selectedDevice) {
      return;
    }

    await dispatch(updateDeviceOwners({
      deviceId: selectedDevice.id,
      userIdList,
    }));

    props.onUpdate();
  }

  const onUpdateDeviceOperators = async (userIdList: number[]) => {
    if (!selectedDevice) {
      return;
    }

    await dispatch(updateDeviceOperators({
      deviceId: selectedDevice.id,
      userIdList,
    }));

    props.onUpdate();
  }

  const onSaveFM = useMemo(() => {
    return debounce((values) => {
      if (selectedDevice) {
        dispatch(assignDevice({
          deviceId: selectedDevice.id,
          userIdList: values,
        }));
      }
    }, 1000);
  }, [ selectedDevice ])

  const onUpdateFrequencyManagerList = (values: number[]) => {
    ownershipFormik.setFieldValue('deviceFrequencyManagerList', values);
    onSaveFM(values);
  }

  return (
    <HorizontalPanel size={HorizontalPanel.SIZES.WHOLE_SPACE} className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label='Device requester'
            variant='outlined'
            name='requester'
            fullWidth
            disabled={true}
            value={ownershipFormik.values.requester}
            onChange={ownershipFormik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label='Last updated by'
            variant='outlined'
            name='lastUpdatedBy'
            fullWidth
            disabled={true}
            value={ownershipFormik.values.lastUpdatedBy}
            onChange={ownershipFormik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <UserSelectionInput
            multiselect
            disabled={isHistorySelected || props.formDisabled}
            label='Owners'
            name='deviceOwnerList'
            fullWidth
            items={Multiselect.getValues(deviceOwnerList)}
            selectedItems={Multiselect.getSelectedValues(deviceOwnerList, ownershipFormik.values.deviceOwnerList)}
            onMultiChange={(values: number[]) => onUpdateDeviceOwners(values)}
          />
        </Grid>
        <Grid item xs={12}>
          <UserSelectionInput
            multiselect
            disabled={isHistorySelected || props.formDisabled}
            label='Operators'
            name='deviceOperators'
            fullWidth
            items={Multiselect.getValues(deviceOperatorList)}
            selectedItems={Multiselect.getSelectedValues(deviceOperatorList, ownershipFormik.values.deviceOperatorList)}
            onMultiChange={(values: number[]) => onUpdateDeviceOperators(values)}
          />
        </Grid>
        <Grid item xs={12}>
          <UserSelectionInput
            multiselect
            disabled={isHistorySelected || props.formDisabled}
            label='Frequency Managers'
            name='deviceFrequencyManagerList'
            fullWidth
            items={Multiselect.getValues(deviceFrequencyManagerList)}
            selectedItems={Multiselect.getSelectedValues(deviceFrequencyManagerList, ownershipFormik.values.deviceFrequencyManagerList)}
            onMultiChange={onUpdateFrequencyManagerList}
          />
        </Grid>
      </Grid>
    </HorizontalPanel>
  );
}

export default DeviceOwnership;